import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Percent,
  Copy,
  ExternalLink,
  Loader2,
  BarChart3,
  Activity,
  Target,
  AlertTriangle,
  Lock,
  Crown
} from 'lucide-react';

function TraderCard({ trader, onCopy }) {
  const winRate = typeof trader.winRate === 'number' ? trader.winRate : null;
  const totalPnL = typeof trader.totalPnL === 'number' ? trader.totalPnL : null;
  const reason = trader.reason;
  const nowSec = Math.floor(Date.now() / 1000);
  const lastSeenAgeSec = reason?.lastSeenTs ? Math.max(0, nowSec - reason.lastSeenTs) : null;
  const lastSeenLabel = lastSeenAgeSec == null
    ? 'Unknown'
    : lastSeenAgeSec < 60
      ? `${Math.round(lastSeenAgeSec)}s ago`
      : lastSeenAgeSec < 3600
        ? `${Math.round(lastSeenAgeSec / 60)}m ago`
        : `${Math.round(lastSeenAgeSec / 3600)}h ago`;
  const windowLabel = reason?.windowSeconds
    ? reason.windowSeconds < 3600
      ? `${Math.round(reason.windowSeconds / 60)}m`
      : `${Math.round(reason.windowSeconds / 3600)}h`
    : null;
  const isDiscovery = !!reason && (winRate == null) && (totalPnL == null);

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
            <span className="text-lg font-bold">
              {trader.address?.slice(2, 4).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium font-mono text-sm">
              {trader.address?.slice(0, 6)}...{trader.address?.slice(-4)}
            </p>
            <a
              href={`https://polymarket.com/profile/${trader.address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
            >
              View on Polymarket <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {!isDiscovery ? (
          <>
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-dark-400" />
                <span className="text-xs text-dark-400">Total P&L</span>
              </div>
              <p
                className={`stat-value text-lg ${
                  (totalPnL ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {(totalPnL ?? 0) >= 0 ? '+' : ''}${(totalPnL ?? 0).toFixed(2)}
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="w-4 h-4 text-dark-400" />
                <span className="text-xs text-dark-400">Win Rate</span>
              </div>
              <p
                className={`stat-value text-lg ${
                  (winRate ?? 0) >= 50 ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {(winRate ?? 0).toFixed(1)}%
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-dark-400" />
                <span className="text-xs text-dark-400">Volume ({windowLabel || 'window'})</span>
              </div>
              <p className="stat-value text-lg text-emerald-400">
                ${((reason?.volume || 0) || 0).toFixed(0)}
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-dark-400" />
                <span className="text-xs text-dark-400">Trades ({windowLabel || 'window'})</span>
              </div>
              <p className="stat-value text-lg">{reason?.trades ?? trader.totalTrades ?? trader.trades ?? 0}</p>
            </div>
          </>
        )}

        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-dark-400" />
            <span className="text-xs text-dark-400">Open Positions</span>
          </div>
          <p className="stat-value text-lg">{trader.openPositions ?? 0}</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-dark-400" />
            <span className="text-xs text-dark-400">Last Seen</span>
          </div>
          <p className="stat-value text-lg">{lastSeenLabel}</p>
        </div>
      </div>

      {reason && (
        <div className="p-3 rounded-lg bg-dark-700/40 border border-dark-700 mb-4">
          <p className="text-xs text-dark-300 font-medium">Why this trader is listed</p>
          <p className="text-xs text-dark-400 mt-1">
            Ranked by volume in the last {windowLabel || 'window'} •
            {` $${(reason.volume || 0).toFixed(0)} vol`} •
            {` ${reason.trades || 0} trades`} •
            {` last seen ${lastSeenLabel}`}
          </p>
          {(trader.openPositions != null || trader.agedOpenPositions != null) && (
            <p className="text-[0.7rem] text-dark-500 mt-1">
              {trader.openPositions != null ? `Open positions: ${trader.openPositions}` : null}
              {trader.openPositions != null && trader.agedOpenPositions != null ? ' • ' : null}
              {trader.agedOpenPositions != null ? `Aged >5d: ${trader.agedOpenPositions}` : null}
            </p>
          )}
        </div>
      )}

      <button
        onClick={() => onCopy(trader)}
        className="btn btn-primary w-full flex items-center justify-center gap-2"
      >
        <Copy className="w-4 h-4" />
        Copy Trader
      </button>
    </div>
  );
}

export default function Traders() {
  const navigate = useNavigate();
  const [searchAddress, setSearchAddress] = useState('');
  const [trader, setTrader] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [topTraders, setTopTraders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTopTraders, setIsLoadingTopTraders] = useState(true);
  const [error, setError] = useState('');
  const [tier, setTier] = useState(null);
  const [isTierLoading, setIsTierLoading] = useState(true);

  // Fetch subscription tier
  useEffect(() => {
    api.get('/payments/subscription')
      .then(res => {
        setTier(res.data?.effectiveTier || res.data?.tier || 'free');
      })
      .catch(() => {
        setTier('free');
      })
      .finally(() => {
        setIsTierLoading(false);
      });
  }, []);

  const isFree = tier === 'free';

  // Fetch top traders on mount (only if not free tier)
  useEffect(() => {
    if (isTierLoading || isFree) {
      setIsLoadingTopTraders(false);
      return;
    }
    
    setIsLoadingTopTraders(true);
    api.get('/traders/top', { params: { limit: 30, windowSeconds: 21600, sampleLimit: 2500 } })
      .then(res => {
        console.log('Top traders:', res.data);
        setTopTraders(res.data || []);
      })
      .catch(err => {
        console.error('Failed to fetch top traders:', err);
      })
      .finally(() => {
        setIsLoadingTopTraders(false);
      });
  }, [isTierLoading, isFree]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchAddress.trim()) return;
    
    setIsLoading(true);
    setError('');
    setTrader(null);
    setAnalytics(null);
    
    try {
      const [traderRes, analyticsRes] = await Promise.all([
        api.get('/traders/search', { params: { address: searchAddress.trim() } }),
        api.get(`/analytics/trader/${searchAddress.trim()}`).catch(() => null)
      ]);
      setTrader(traderRes.data);
      if (analyticsRes?.data) {
        setAnalytics(analyticsRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to find trader');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (trader) => {
    navigate('/copy-configs', { state: { trader } });
  };

  // Loading state for tier check
  if (isTierLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  // Free tier locked screen
  if (isFree) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Find Traders</h1>
            <p className="text-dark-400">Discover and analyze top traders to copy</p>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-dark-800/70 border border-dark-700 text-sm">
            <Lock className="w-4 h-4 text-amber-400" />
            <span className="text-dark-200">Locked</span>
          </div>
        </div>

        <div className="card card-glow">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600/30 to-accent-600/20 border border-primary-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">Upgrade to Unlock Trader Discovery</h2>
              <p className="text-dark-400 mt-1">
                Find Traders is a premium feature that helps you discover profitable traders to copy.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Search any wallet</span>
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Top trader discovery</span>
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Advanced analytics</span>
                <span className="px-3 py-1 rounded-full text-xs bg-dark-800 border border-dark-700 text-dark-300">Performance metrics</span>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <Link to="/pricing" className="btn btn-gradient">
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Link>
                <Link to="/pricing" className="text-sm text-dark-400 hover:text-primary-300 transition-colors">
                  View plans
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Preview of what they're missing */}
        <div className="card opacity-50 pointer-events-none relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/80 to-transparent z-10 flex items-center justify-center">
            <div className="text-center">
              <Lock className="w-8 h-8 text-dark-400 mx-auto mb-2" />
              <p className="text-dark-300 font-medium">Upgrade to see top traders</p>
            </div>
          </div>
          <div className="blur-sm">
            <h3 className="text-sm font-medium text-dark-300 mb-4">Trader Discovery Preview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 bg-dark-700/50 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-dark-600 rounded-full"></div>
                    <div>
                      <div className="h-4 w-24 bg-dark-600 rounded"></div>
                      <div className="h-3 w-16 bg-dark-600 rounded mt-1"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-12 bg-dark-600 rounded"></div>
                    <div className="h-12 bg-dark-600 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Find Traders</h1>
        <p className="text-dark-400">Search for traders by their wallet address to copy their trades</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="card">
        <label className="label">Trader Wallet Address</label>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              placeholder="0x..."
              className="input pl-10"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !searchAddress.trim()}
            className="btn btn-primary px-6"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Search'
            )}
          </button>
        </div>
        
        {error && (
          <p className="text-red-400 text-sm mt-2">{error}</p>
        )}
        
        <p className="text-dark-500 text-sm mt-2">
          Tip: You can find trader addresses on{' '}
          <a
            href="https://polymarket.com/leaderboard"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-400 hover:text-primary-300"
          >
            Polymarket Leaderboard
          </a>
        </p>
      </form>

      {/* Results */}
      {trader && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <TraderCard trader={trader} onCopy={handleCopy} />
          </div>
          
          {/* Advanced Analytics */}
          {analytics && (
            <div className="card">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-400" />
                Advanced Analytics
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-dark-400" />
                    <span className="text-xs text-dark-400">Trading Style</span>
                  </div>
                  <p className="stat-value text-lg capitalize">
                    {analytics.tradingStyle?.style?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
                
                <div className="stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-dark-400" />
                    <span className="text-xs text-dark-400">Trades/Day</span>
                  </div>
                  <p className="stat-value text-lg">
                    {analytics.tradingStyle?.tradesPerDay?.toFixed(1) || '0'}
                  </p>
                </div>
                
                <div className="stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-dark-400" />
                    <span className="text-xs text-dark-400">Risk Score</span>
                  </div>
                  <p className={`stat-value text-lg ${
                    (analytics.riskMetrics?.riskScore || 5) > 7 ? 'text-red-400' : 
                    (analytics.riskMetrics?.riskScore || 5) > 4 ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    {analytics.riskMetrics?.riskScore || 5}/10
                  </p>
                </div>
                
                <div className="stat-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-dark-400" />
                    <span className="text-xs text-dark-400">Unique Markets</span>
                  </div>
                  <p className="stat-value text-lg">
                    {analytics.marketPreferences?.uniqueMarkets || 0}
                  </p>
                </div>
              </div>
              
              {/* Performance */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-dark-700/50 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Last 24h</h4>
                  <p className="text-2xl font-bold">{analytics.performance?.last24h?.trades || 0} trades</p>
                  <p className="text-sm text-dark-400">${(analytics.performance?.last24h?.volume || 0).toFixed(2)} volume</p>
                </div>
                
                <div className="p-4 bg-dark-700/50 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Last 7 Days</h4>
                  <p className="text-2xl font-bold">{analytics.performance?.last7d?.trades || 0} trades</p>
                  <p className="text-sm text-dark-400">${(analytics.performance?.last7d?.volume || 0).toFixed(2)} volume</p>
                </div>
                
                <div className="p-4 bg-dark-700/50 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Last 30 Days</h4>
                  <p className="text-2xl font-bold">{analytics.performance?.last30d?.trades || 0} trades</p>
                  <p className="text-sm text-dark-400">${(analytics.performance?.last30d?.volume || 0).toFixed(2)} volume</p>
                </div>
              </div>
              
              {/* Recent Activity */}
              {analytics.recentActivity && (
                <div className="mt-4 p-4 bg-dark-700/50 rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Recent Activity</h4>
                  <div className="flex items-center gap-4">
                    <span className={`badge ${analytics.recentActivity.isActive ? 'badge-success' : 'badge-warning'}`}>
                      {analytics.recentActivity.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {analytics.recentActivity.lastTradeAge && (
                      <span className="text-sm text-dark-400">
                        Last trade: {analytics.recentActivity.lastTradeAge < 60 
                          ? `${Math.round(analytics.recentActivity.lastTradeAge)} min ago`
                          : `${Math.round(analytics.recentActivity.lastTradeAge / 60)} hours ago`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent Positions */}
      {trader?.recentTrades?.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-4">Recent Trades</h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Market</th>
                  <th>Side</th>
                  <th>Size</th>
                  <th>Price</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {trader.recentTrades.map((trade, i) => (
                  <tr key={i}>
                    <td className="max-w-[200px] truncate">{trade.title || 'Unknown'}</td>
                    <td>
                      <span className={`badge ${trade.side === 'BUY' ? 'badge-success' : 'badge-danger'}`}>
                        {trade.side}
                      </span>
                    </td>
                    <td>{trade.size?.toFixed(2)}</td>
                    <td>${trade.price?.toFixed(2)}</td>
                    <td className="text-dark-400">
                      {new Date(trade.timestamp * 1000).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State with Sample Traders */}
      {!trader && !isLoading && (
        <div className="space-y-6">
          <div className="card text-center py-8">
            <Users className="w-10 h-10 mx-auto mb-3 text-dark-500" />
            <h3 className="text-lg font-medium mb-2">Search for a Trader</h3>
            <p className="text-dark-400 text-sm max-w-md mx-auto">
              Enter a Polymarket wallet address to view their trading stats and start copying their trades.
            </p>
          </div>
          
          {/* Recently Active Traders - with loading state */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-dark-300">Trader Discovery</h3>
              <span className="text-xs text-dark-500">Ranked by volume</span>
            </div>
            <p className="text-xs text-dark-500 mb-3">
              ⚠️ Not recommendations. Past performance ≠ future results. DYOR.
            </p>
            
            {isLoadingTopTraders ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400 mx-auto mb-3" />
                <p className="text-dark-400 text-sm">Discovering active traders...</p>
                <p className="text-dark-500 text-xs mt-1">This may take a moment</p>
              </div>
            ) : topTraders.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topTraders.map((t, i) => (
                    <div key={t.address || i} onClick={() => setSearchAddress(t.address)} className="cursor-pointer">
                      <TraderCard trader={t} onCopy={handleCopy} />
                    </div>
                  ))}
                </div>
                <a
                  href="https://polymarket.com/leaderboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-xs text-primary-400 hover:text-primary-300 mt-4"
                >
                  Full leaderboard →
                </a>
              </>
            ) : (
              <div className="py-8 text-center">
                <Users className="w-8 h-8 text-dark-500 mx-auto mb-2" />
                <p className="text-dark-400 text-sm">No traders found</p>
                <p className="text-dark-500 text-xs mt-1">Try searching for a specific wallet address above</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
